import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button, IconButton } from '../../components/ui/Button'
import { Card, CardBody, CardHeader, ProgressBar } from '../../components/ui/Card'
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input, Textarea, Toggle } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Table, THead, Th, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { STATUS_MANUTENCAO_LABEL, StatusVeiculo } from '../../lib/enums'
import { formatDate, formatMoney } from '../../lib/utils'
import { BloqueioRecurso } from '../plano/BloqueioRecurso'
import { useRecurso } from '../plano/useRecurso'
import type { ItemManutencao, RegistroManutencao, SalvarItemManutencaoRequest, SalvarRegistroManutencaoRequest, SalvarRegistroManutencaoResultado } from '../../types'
import { FiltroPeriodo } from './FiltroPeriodo'
import { PagamentoVeiculoForm } from './PagamentoVeiculoForm'
import { montarPagamento, pagamentoValido, pagamentoVazio, type PagamentoForm } from './pagamentoUtils'
import { formatKm, hojeIso, invalidarVeiculo, lerNumero, soData, toneStatus } from './veiculoUtils'

const AVISO_TIPICO = 'Intervalos típicos, editáveis. Confira o manual do seu veículo: ele manda no seu carro.'

function intervaloTexto(km?: number | null, meses?: number | null) {
  const partes = [km ? `${new Intl.NumberFormat('pt-BR').format(km)} km` : null, meses ? `${meses} ${meses === 1 ? 'mês' : 'meses'}` : null].filter(Boolean)
  return partes.length ? `A cada ${partes.join(' ou ')}` : 'Sem intervalo definido'
}

function ultimaTexto(i: ItemManutencao) {
  const partes = [i.ultimaData ? formatDate(i.ultimaData) : null, i.ultimaKm != null ? `aos ${formatKm(i.ultimaKm)}` : null].filter(Boolean)
  return partes.length ? `última em ${partes.join(' ')}` : 'sem troca registrada'
}

export function ManutencoesPage() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const toast = useToast()
  const modulo = useRecurso('veiculos.modulo')
  const manutencao = useRecurso('veiculos.manutencao')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [baixando, setBaixando] = useState<ItemManutencao | null>(null)
  const [editandoRegistro, setEditandoRegistro] = useState<RegistroManutencao | null>(null)
  const [itemForm, setItemForm] = useState<{ item?: ItemManutencao } | null>(null)
  const [catalogoAberto, setCatalogoAberto] = useState(false)
  const [excluindoItem, setExcluindoItem] = useState<ItemManutencao | null>(null)
  const [excluindoRegistro, setExcluindoRegistro] = useState<RegistroManutencao | null>(null)

  const ativo = !!id && modulo.habilitado && manutencao.habilitado
  const { data: v, isLoading: carregandoV, isError: erroV, error } = useQuery({
    queryKey: ['veiculos', id], queryFn: () => veiculosApi.obter(id), enabled: !!id && modulo.habilitado,
  })
  const { data: itens = [], isLoading: carregandoItens, isError: erroItens } = useQuery({
    queryKey: ['veiculos', id, 'manutencao', 'itens'], queryFn: () => veiculosApi.manutencao.itens(id), enabled: ativo,
  })
  const { data: historico = [], isLoading: carregandoHist } = useQuery({
    queryKey: ['veiculos', id, 'manutencoes', de, ate],
    queryFn: () => veiculosApi.manutencoes.list(id, { de: de || undefined, ate: ate || undefined }), enabled: ativo,
  })

  const removerItem = useMutation({
    mutationFn: (i: ItemManutencao) => veiculosApi.manutencao.removerItem(id, i.id),
    onSuccess: () => { invalidarVeiculo(qc); toast.success('Item excluído.'); setExcluindoItem(null) },
    onError: (e: Error) => toast.error(e.message),
  })
  const removerRegistro = useMutation({
    mutationFn: (r: RegistroManutencao) => veiculosApi.manutencoes.remover(id, r.id),
    onSuccess: () => { invalidarVeiculo(qc); toast.success('Manutenção excluída.'); setExcluindoRegistro(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  const titulo = 'Manutenções'
  if (!modulo.habilitado) return <><PageHeader title={titulo} /><BloqueioRecurso mensagem="O módulo de veículos não está incluído no seu plano." /></>
  if (!manutencao.habilitado) return <><PageHeader title={titulo} /><BloqueioRecurso mensagem="O plano de manutenção e os lembretes de troca não estão incluídos no seu plano." /></>
  if (carregandoV) return <><PageHeader title={titulo} /><Card><LoadingState /></Card></>
  if (erroV || !v) return <><PageHeader title={titulo} /><Card><ErrorState message={(error as Error)?.message || 'Veículo não encontrado.'} /></Card></>

  const podeAgir = v.podeEditar && v.status === StatusVeiculo.Ativo
  const temTipico = itens.some((i) => i.tipico)
  const totalHist = historico.reduce((s, r) => s + r.valor, 0)

  return (
    <>
      <PageHeader title={titulo} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{v.apelido}</h3>
          <p className="text-13 text-muted">{v.modelo.marca} {v.modelo.modelo} · {formatKm(v.kmAtual)}</p>
        </div>
        {podeAgir && (
          <div className="flex flex-wrap gap-2">
            <Button variant="light" icon="ri-list-check-2" onClick={() => setCatalogoAberto(true)}>Itens do catálogo</Button>
            <Button icon="ri-add-line" onClick={() => setItemForm({})}>Novo item</Button>
          </div>
        )}
      </div>

      {temTipico && <p className="mb-4 rounded bg-info/10 px-3 py-2.5 text-13 text-info"><i className="ri-information-line" aria-hidden /> {AVISO_TIPICO}</p>}

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {carregandoItens ? <Card><LoadingState /></Card> : erroItens ? <Card><ErrorState message="Não foi possível carregar os itens." /></Card> : itens.length === 0 ? (
            <Card>
              <EmptyState icon="ri-tools-line" message="Nenhum item de manutenção. Comece pelos itens típicos do seu tipo de veículo (óleo, filtros, pastilhas, pneus...) e ajuste os intervalos."
                action={podeAgir ? <div className="flex flex-wrap justify-center gap-2"><Button variant="soft-primary" icon="ri-list-check-2" onClick={() => setCatalogoAberto(true)}>Escolher itens do catálogo</Button><Button variant="light" icon="ri-add-line" onClick={() => setItemForm({})}>Criar item</Button></div> : undefined} />
            </Card>
          ) : itens.map((i) => (
            <Card key={i.id} className={i.ativo ? '' : 'opacity-60'}>
              <CardBody className="space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-medium text-white">{i.nome}</h4>
                      <Badge variant={toneStatus(i.status)}>{STATUS_MANUTENCAO_LABEL[i.status]}</Badge>
                      {i.tipico && <Badge variant="muted">Típico</Badge>}
                      {!i.ativo && <Badge variant="muted">Pausado</Badge>}
                    </div>
                    <p className="mt-1 text-13 text-white">{i.previsao.texto}</p>
                    <p className="text-xs text-muted">{intervaloTexto(i.intervaloKm, i.intervaloMeses)} · {ultimaTexto(i)}</p>
                  </div>
                  {podeAgir && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="soft-primary" icon="ri-check-line" disabled={!i.ativo} onClick={() => setBaixando(i)}>Dar baixa</Button>
                      <IconButton icon="ri-edit-line" label="Editar" onClick={() => setItemForm({ item: i })} />
                      <IconButton icon="ri-delete-bin-line" label="Excluir" tone="danger" onClick={() => setExcluindoItem(i)} />
                    </div>
                  )}
                </div>
                <ProgressBar value={i.progresso * 100} tone={toneStatus(i.status)} />
                {i.previsao.semBase && <p className="text-xs text-warning">Falta a última troca (km ou data): informe em Editar para ter a previsão.</p>}
              </CardBody>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader title="Ao dar baixa" icon="ri-information-line" />
          <CardBody className="space-y-2 text-13 text-muted">
            <p>Pede data, km, valor e oficina. Cria o lançamento &quot;Manutenção&quot; na conta ou no cartão que você escolher, registra o km no histórico e reinicia a contagem do item.</p>
            <p>O aviso vem antes de vencer (por padrão 500 km ou 15 dias), ajustável em cada item.</p>
          </CardBody>
        </Card>
      </div>

      <div className="mb-4 mt-8 flex flex-wrap items-end justify-between gap-3">
        <h3 className="text-lg font-semibold text-white">Histórico de manutenções</h3>
        <FiltroPeriodo de={de} ate={ate} onChange={(d, a) => { setDe(d); setAte(a) }} />
      </div>
      <Card>
        {carregandoHist ? <LoadingState /> : historico.length === 0 ? (
          <EmptyState icon="ri-history-line" message={de || ate ? 'Nenhuma manutenção no período.' : 'Nenhuma manutenção registrada ainda.'} />
        ) : (
          <>
            <Table>
              <THead><tr><Th>Data</Th><Th>Descrição</Th><Th className="text-right">Km</Th><Th className="text-right">Valor</Th><Th>Oficina</Th><Th className="w-24" /></tr></THead>
              <tbody>
                {historico.map((r) => (
                  <tr key={r.id} className="border-t border-border/70">
                    <Td>{formatDate(r.data)}</Td>
                    <Td>{r.descricao}{r.itemNome && r.itemNome !== r.descricao && <p className="text-2xs text-muted">{r.itemNome}</p>}</Td>
                    <Td className="text-right">{formatKm(r.odometro)}</Td>
                    <Td className="text-right font-medium text-white">{formatMoney(r.valor)}</Td>
                    <Td>{r.oficina || '—'}</Td>
                    <Td>
                      {podeAgir && (
                        <div className="flex justify-end">
                          <IconButton icon="ri-edit-line" label="Editar" onClick={() => setEditandoRegistro(r)} />
                          <IconButton icon="ri-delete-bin-line" label="Excluir" tone="danger" onClick={() => setExcluindoRegistro(r)} />
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="border-t border-border/70 px-4 py-3 text-right text-13 text-muted">{historico.length} registro(s) · <span className="font-medium text-white">{formatMoney(totalHist)}</span></p>
          </>
        )}
      </Card>

      <p className="mt-6 text-xs text-muted"><Link to={`/veiculos/${v.id}`} className="hover:text-primary"><i className="ri-arrow-left-line" aria-hidden /> Voltar para {v.apelido}</Link></p>

      {(baixando || editandoRegistro) && (
        <BaixaModal veiculoId={v.id} kmAtual={v.kmAtual} item={baixando ?? undefined} registro={editandoRegistro ?? undefined}
          onClose={() => { setBaixando(null); setEditandoRegistro(null) }} />
      )}
      {itemForm && <ItemFormModal veiculoId={v.id} item={itemForm.item} onClose={() => setItemForm(null)} />}
      {catalogoAberto && <CatalogoModal veiculoId={v.id} onClose={() => setCatalogoAberto(false)} />}

      <Modal open={!!excluindoItem} onClose={() => setExcluindoItem(null)} size="sm" title="Excluir item"
        footer={<><Button variant="light" onClick={() => setExcluindoItem(null)}>Cancelar</Button><Button variant="danger" icon="ri-delete-bin-line" loading={removerItem.isPending} onClick={() => excluindoItem && removerItem.mutate(excluindoItem)}>Excluir</Button></>}>
        {excluindoItem && <p className="text-13 text-muted">Excluir o item <span className="text-white">{excluindoItem.nome}</span>? As manutenções já registradas ficam no histórico.</p>}
      </Modal>
      <Modal open={!!excluindoRegistro} onClose={() => setExcluindoRegistro(null)} size="sm" title="Excluir manutenção"
        footer={<><Button variant="light" onClick={() => setExcluindoRegistro(null)}>Cancelar</Button><Button variant="danger" icon="ri-delete-bin-line" loading={removerRegistro.isPending} onClick={() => excluindoRegistro && removerRegistro.mutate(excluindoRegistro)}>Excluir</Button></>}>
        {excluindoRegistro && (
          <p className="text-13 text-muted">
            Excluir <span className="text-white">{excluindoRegistro.descricao}</span> de {formatMoney(excluindoRegistro.valor)}?
            {excluindoRegistro.transacaoId ? ' O lançamento ligado também será excluído.' : ''}
          </p>
        )}
      </Modal>
    </>
  )
}

/** Dar baixa num item (criar) ou corrigir um registro do histórico (editar). */
function BaixaModal({ veiculoId, kmAtual, item, registro: r, onClose }: {
  veiculoId: string; kmAtual: number; item?: ItemManutencao; registro?: RegistroManutencao; onClose: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const editando = !!r
  const [data, setData] = useState(r ? soData(r.data) : hojeIso())
  const [km, setKm] = useState(r ? String(r.odometro) : String(kmAtual))
  const [descricao, setDescricao] = useState(r?.descricao ?? '')
  const [valor, setValor] = useState(r ? String(r.valor) : '')
  const [oficina, setOficina] = useState(r?.oficina ?? '')
  const [obs, setObs] = useState(r?.observacao ?? '')
  const [pagamento, setPagamento] = useState<PagamentoForm>(pagamentoVazio())
  const [trocarPagamento, setTrocarPagamento] = useState(!editando)
  const [alerta, setAlerta] = useState<SalvarRegistroManutencaoResultado | null>(null)

  const v = lerNumero(valor) ?? 0
  const kmNum = lerNumero(km)
  const exigePagamento = v > 0 && trocarPagamento
  const valido = !!data && kmNum != null && kmNum >= 0 && v >= 0 && (!exigePagamento || pagamentoValido(pagamento))
  const nome = item?.nome ?? r?.itemNome ?? 'Manutenção'

  const salvar = useMutation({
    mutationFn: (confirmar: boolean) => {
      const req: SalvarRegistroManutencaoRequest = {
        itemId: item?.id ?? r?.itemId ?? null, data, odometro: kmNum ?? 0, descricao: descricao.trim() || null, valor: v,
        oficina: oficina.trim() || null, observacao: obs.trim() || null,
        pagamento: exigePagamento ? montarPagamento(pagamento) : null,
        confirmarRegressao: confirmar || undefined,
      }
      return r ? veiculosApi.manutencoes.atualizar(veiculoId, r.id, req) : veiculosApi.manutencoes.criar(veiculoId, req)
    },
    onSuccess: (res) => {
      if (!res.gravado) { setAlerta(res); return }
      invalidarVeiculo(qc)
      toast.success(editando ? 'Manutenção atualizada.' : 'Baixa registrada.')
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal open onClose={onClose} size="lg" title={editando ? 'Editar manutenção' : `Dar baixa: ${nome}`}
      footer={alerta
        ? <><Button variant="light" onClick={() => setAlerta(null)}>Corrigir</Button><Button loading={salvar.isPending} onClick={() => salvar.mutate(true)}>Gravar mesmo assim</Button></>
        : <><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate(false)}>Salvar</Button></>}>
      <div className="space-y-4">
        {!editando && <p className="text-13 text-muted">Cria o lançamento &quot;Manutenção&quot; (se houver valor), registra o km e reinicia a contagem do item.</p>}
        <div className="grid gap-3 sm:grid-cols-3">
          <Input label="Data" type="date" max={hojeIso()} value={data} disabled={!!alerta} onChange={(e) => setData(e.target.value)} />
          <Input label="Km *" type="number" inputMode="numeric" min={0} value={km} disabled={!!alerta} autoFocus onChange={(e) => setKm(e.target.value)} />
          <Input label="Valor (R$)" type="number" inputMode="decimal" min={0} step="0.01" value={valor} disabled={!!alerta} onChange={(e) => setValor(e.target.value)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Descrição (opcional)" placeholder={nome} value={descricao} disabled={!!alerta} onChange={(e) => setDescricao(e.target.value)} />
          <Input label="Oficina (opcional)" value={oficina} disabled={!!alerta} onChange={(e) => setOficina(e.target.value)} />
        </div>
        {v > 0 && (
          <div className="space-y-2">
            <span className="form-label">Pagar com</span>
            {editando && (
              <label className="flex items-center gap-2 text-13 text-white">
                <input type="checkbox" checked={trocarPagamento} onChange={(e) => setTrocarPagamento(e.target.checked)} /> Trocar a forma de pagamento
              </label>
            )}
            {trocarPagamento && <PagamentoVeiculoForm valor={pagamento} onChange={setPagamento} comParcelas valorTotal={v} />}
          </div>
        )}
        <Textarea label="Observação (opcional)" rows={2} value={obs} disabled={!!alerta} onChange={(e) => setObs(e.target.value)} />
        {alerta && (
          <div className="rounded bg-warning/10 px-3 py-2.5 text-13 text-warning" role="alert">
            <i className="ri-error-warning-line" aria-hidden /> {alerta.mensagem || 'Esse km parece diferente do esperado.'}
            {alerta.kmAnterior != null && <p className="mt-1 text-xs">Último km registrado: {formatKm(alerta.kmAnterior)}.</p>}
            <p className="mt-1 text-xs">Nada foi salvo ainda. Confirme só se o valor estiver certo.</p>
          </div>
        )}
      </div>
    </Modal>
  )
}

function ItemFormModal({ veiculoId, item: i, onClose }: { veiculoId: string; item?: ItemManutencao; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [nome, setNome] = useState(i?.nome ?? '')
  const [intKm, setIntKm] = useState(i?.intervaloKm != null ? String(i.intervaloKm) : '')
  const [intMeses, setIntMeses] = useState(i?.intervaloMeses != null ? String(i.intervaloMeses) : '')
  const [ultKm, setUltKm] = useState(i?.ultimaKm != null ? String(i.ultimaKm) : '')
  const [ultData, setUltData] = useState(i?.ultimaData ? soData(i.ultimaData) : '')
  const [avisoKm, setAvisoKm] = useState(String(i?.avisoKm ?? 500))
  const [avisoDias, setAvisoDias] = useState(String(i?.avisoDias ?? 15))
  const [ativo, setAtivo] = useState(i?.ativo ?? true)

  const kmI = lerNumero(intKm)
  const mesesI = lerNumero(intMeses)
  const valido = nome.trim() !== '' && (!!kmI || !!mesesI) && (kmI == null || kmI > 0) && (mesesI == null || mesesI > 0)

  const salvar = useMutation({
    mutationFn: () => {
      const req: SalvarItemManutencaoRequest = {
        nome: nome.trim(), intervaloKm: kmI ?? null, intervaloMeses: mesesI != null ? Math.floor(mesesI) : null,
        ultimaKm: lerNumero(ultKm) ?? null, ultimaData: ultData || null,
        avisoKm: Math.max(0, lerNumero(avisoKm) ?? 500), avisoDias: Math.max(0, Math.floor(lerNumero(avisoDias) ?? 15)), ativo,
      }
      return i ? veiculosApi.manutencao.atualizarItem(veiculoId, i.id, req) : veiculosApi.manutencao.criarItem(veiculoId, req)
    },
    onSuccess: () => { invalidarVeiculo(qc); toast.success(i ? 'Item atualizado.' : 'Item criado.'); onClose() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal open onClose={onClose} size="lg" title={i ? 'Editar item' : 'Novo item de manutenção'}
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button></>}>
      <div className="space-y-4">
        <Input label="Nome" value={nome} maxLength={120} autoFocus onChange={(e) => setNome(e.target.value)} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="A cada (km)" type="number" inputMode="numeric" min={0} value={intKm} onChange={(e) => setIntKm(e.target.value)} />
          <Input label="A cada (meses)" type="number" inputMode="numeric" min={0} value={intMeses} onChange={(e) => setIntMeses(e.target.value)} />
        </div>
        <p className="-mt-2 text-xs text-muted">Informe km, meses ou os dois: vence o que vier primeiro.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Última troca: km (opcional)" type="number" inputMode="numeric" min={0} value={ultKm} onChange={(e) => setUltKm(e.target.value)} />
          <Input label="Última troca: data (opcional)" type="date" max={hojeIso()} value={ultData} onChange={(e) => setUltData(e.target.value)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Avisar antes (km)" type="number" inputMode="numeric" min={0} value={avisoKm} onChange={(e) => setAvisoKm(e.target.value)} />
          <Input label="Avisar antes (dias)" type="number" inputMode="numeric" min={0} value={avisoDias} onChange={(e) => setAvisoDias(e.target.value)} />
        </div>
        <Toggle label="Item ativo (entra nos avisos)" checked={ativo} onChange={setAtivo} />
      </div>
    </Modal>
  )
}

function CatalogoModal({ veiculoId, onClose }: { veiculoId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [marcados, setMarcados] = useState<Record<string, boolean>>({})
  const { data, isLoading, isError } = useQuery({ queryKey: ['veiculos', veiculoId, 'manutencao', 'catalogo'], queryFn: () => veiculosApi.manutencao.catalogo(veiculoId) })

  const disponiveis = (data?.itens ?? []).filter((x) => !x.jaExiste)
  // Por padrão todos os que faltam vêm marcados, até a pessoa mexer.
  const marcado = (nome: string) => marcados[nome] ?? true
  const escolhidos = disponiveis.filter((x) => marcado(x.nome)).map((x) => x.nome)

  const carregar = useMutation({
    mutationFn: () => veiculosApi.manutencao.carregarCatalogo(veiculoId, { nomes: escolhidos }),
    onSuccess: (r) => { invalidarVeiculo(qc); toast.success(`${r.length} ${r.length === 1 ? 'item adicionado' : 'itens adicionados'}.`); onClose() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal open onClose={onClose} size="lg" title="Itens do catálogo típico"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-add-line" disabled={escolhidos.length === 0} loading={carregar.isPending} onClick={() => carregar.mutate()}>Adicionar {escolhidos.length} {escolhidos.length === 1 ? 'item' : 'itens'}</Button></>}>
      {isLoading ? <LoadingState /> : isError || !data ? <ErrorState message="Não foi possível carregar o catálogo." /> : (
        <div className="space-y-3">
          <p className="rounded bg-info/10 px-3 py-2.5 text-13 text-info"><i className="ri-information-line" aria-hidden /> {data.aviso || AVISO_TIPICO}</p>
          <p className="text-xs text-muted">A contagem começa no km e na data de hoje. Depois você ajusta cada item.</p>
          <ul className="divide-y divide-border/60">
            {data.itens.map((x) => (
              <li key={x.nome}>
                <label className={`flex items-center gap-3 py-2.5 ${x.jaExiste ? 'opacity-50' : 'cursor-pointer'}`}>
                  <input type="checkbox" disabled={x.jaExiste} checked={!x.jaExiste && marcado(x.nome)} onChange={(e) => setMarcados((m) => ({ ...m, [x.nome]: e.target.checked }))} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-13 text-white">{x.nome}</span>
                    <span className="block text-xs text-muted">{intervaloTexto(x.intervaloKm, x.intervaloMeses)}</span>
                  </span>
                  {x.jaExiste && <Badge variant="muted">Já cadastrado</Badge>}
                </label>
              </li>
            ))}
          </ul>
          {disponiveis.length === 0 && <p className="text-13 text-muted">Todos os itens do catálogo já estão no seu plano.</p>}
        </div>
      )}
    </Modal>
  )
}
